import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Globe, Building2, ArrowLeft, ExternalLink, Star, Crown, Edit } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BusinessDetail() {
  const { id } = useParams();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBusiness();
  }, [id]);

  const fetchBusiness = async () => {
    try {
      const res = await axios.get(`${API}/businesses/${id}`);
      setBusiness(res.data);
    } catch (e) {
      setError("Entreprise non trouvée");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-lagoon border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !business) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-coral/10 flex items-center justify-center mx-auto mb-6">
            <Building2 className="w-10 h-10 text-coral" />
          </div>
          <h1 className="font-serif text-2xl font-bold text-lagoon-900 mb-4">{error}</h1>
          <Link to="/recherche" className="btn-primary inline-block" data-testid="back-to-search">
            Retour à la recherche
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-lagoon-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="home-logo">
            <div className="w-10 h-10 rounded-full bg-lagoon flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <span className="font-serif text-xl font-semibold text-lagoon-900">Annuaire Polynésien</span>
          </Link>
          <Link to="/inscrire" className="btn-coral text-sm py-2 px-6" data-testid="register-btn">
            Inscrire mon entreprise
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-28 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Back Link */}
          <Link 
            to="/recherche" 
            className="inline-flex items-center gap-2 text-lagoon-600 hover:text-lagoon-900 mb-8"
            data-testid="back-to-search"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Retour aux résultats</span>
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-3xl overflow-hidden"
          >
            {/* Header Section */}
            <div className="bg-gradient-to-r from-lagoon to-lagoon-900 p-8 md:p-12 relative">
              {/* Featured Badge */}
              {business.is_featured && (
                <div className="absolute top-6 right-6 flex items-center gap-2 px-4 py-2 bg-golden text-lagoon-900 rounded-full text-sm font-bold shadow-lg">
                  <Star className="w-4 h-4 fill-lagoon-900" />
                  Entreprise en vedette
                </div>
              )}
              
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                {business.logo_base64 ? (
                  <img 
                    src={business.logo_base64} 
                    alt={business.name}
                    className="w-24 h-24 md:w-32 md:h-32 rounded-2xl object-cover border-4 border-white/20"
                    data-testid="business-logo"
                  />
                ) : (
                  <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl logo-placeholder text-4xl border-4 border-white/20" data-testid="business-logo-placeholder">
                    {business.name.charAt(0)}
                  </div>
                )}
                <div className="flex-1">
                  <span className="inline-block px-3 py-1 bg-white/20 text-white rounded-full text-sm mb-3">
                    {business.category_name}
                  </span>
                  <h1 className="font-serif text-3xl md:text-4xl font-bold text-white mb-2" data-testid="business-name">
                    {business.name}
                  </h1>
                  {business.address && (
                    <div className="flex items-center gap-2 text-white/80">
                      <MapPin className="w-4 h-4" />
                      <span>{business.address}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Content Section */}
            <div className="p-8 md:p-12">
              {/* Description */}
              <div className="mb-10">
                <h2 className="font-serif text-xl font-semibold text-lagoon-900 mb-4">
                  À propos
                </h2>
                <p className="text-gray-600 leading-relaxed" data-testid="business-description">
                  {business.description}
                </p>
              </div>

              {/* Contact Information */}
              <div className="mb-10">
                <h2 className="font-serif text-xl font-semibold text-lagoon-900 mb-4">
                  Coordonnées
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {business.phone && (
                    <a 
                      href={`tel:${business.phone}`}
                      className="flex items-center gap-4 p-4 bg-lagoon-50 rounded-xl hover:bg-lagoon-100 transition-colors"
                      data-testid="business-phone"
                    >
                      <div className="w-12 h-12 rounded-full bg-lagoon flex items-center justify-center">
                        <Phone className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Téléphone</p>
                        <p className="font-semibold text-lagoon-900">{business.phone}</p>
                      </div>
                    </a>
                  )}
                  
                  {business.email && (
                    <a 
                      href={`mailto:${business.email}`}
                      className="flex items-center gap-4 p-4 bg-lagoon-50 rounded-xl hover:bg-lagoon-100 transition-colors"
                      data-testid="business-email"
                    >
                      <div className="w-12 h-12 rounded-full bg-lagoon flex items-center justify-center">
                        <Mail className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="font-semibold text-lagoon-900">{business.email}</p>
                      </div>
                    </a>
                  )}
                  
                  {business.website && (
                    <a 
                      href={business.website.startsWith('http') ? business.website : `https://${business.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-4 p-4 bg-lagoon-50 rounded-xl hover:bg-lagoon-100 transition-colors"
                      data-testid="business-website"
                    >
                      <div className="w-12 h-12 rounded-full bg-lagoon flex items-center justify-center">
                        <Globe className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-500">Site web</p>
                        <p className="font-semibold text-lagoon-900 truncate">{business.website}</p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-lagoon" />
                    </a>
                  )}
                  
                  {business.address && (
                    <div className="flex items-center gap-4 p-4 bg-lagoon-50 rounded-xl">
                      <div className="w-12 h-12 rounded-full bg-lagoon flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Adresse</p>
                        <p className="font-semibold text-lagoon-900" data-testid="business-address">{business.address}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* CTA */}
              <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-100">
                {business.phone && (
                  <a href={`tel:${business.phone}`} className="flex-1">
                    <Button className="btn-coral w-full" data-testid="call-btn">
                      <Phone className="w-4 h-4 mr-2" />
                      Appeler
                    </Button>
                  </a>
                )}
                {business.email && (
                  <a href={`mailto:${business.email}`} className="flex-1">
                    <Button variant="outline" className="w-full border-lagoon text-lagoon hover:bg-lagoon-50" data-testid="email-btn">
                      <Mail className="w-4 h-4 mr-2" />
                      Envoyer un email
                    </Button>
                  </a>
                )}
              </div>

              {/* Boost CTA - Contact for featuring */}
              {!business.is_featured && (
                <div className="mt-6 p-6 bg-gradient-to-r from-golden/10 to-golden/5 rounded-2xl border border-golden/20">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-golden/20 flex items-center justify-center flex-shrink-0">
                      <Crown className="w-6 h-6 text-golden" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-serif text-lg font-semibold text-lagoon-900 mb-1">
                        C'est votre entreprise ?
                      </h4>
                      <p className="text-gray-600 text-sm mb-4">
                        Boostez votre visibilité en apparaissant en page d'accueil avec le badge "En vedette". Contactez-nous pour en savoir plus.
                      </p>
                      <div className="flex flex-wrap gap-3">
                        <a href="mailto:contact@annuaire-polynesien.pf?subject=Mise en avant - ${encodeURIComponent(business.name)}">
                          <Button className="bg-golden text-lagoon-900 hover:bg-golden/90" data-testid="contact-boost-btn">
                            <Mail className="w-4 h-4 mr-2" />
                            Nous contacter
                          </Button>
                        </a>
                        <Link to="/modifier-fiche">
                          <Button variant="outline" className="border-lagoon text-lagoon hover:bg-lagoon-50" data-testid="edit-profile-btn">
                            <Edit className="w-4 h-4 mr-2" />
                            Modifier ma fiche
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Edit link for featured businesses */}
              {business.is_featured && (
                <div className="mt-6 text-center">
                  <Link 
                    to="/modifier-fiche" 
                    className="text-lagoon hover:text-lagoon-600 text-sm inline-flex items-center gap-2"
                    data-testid="edit-link"
                  >
                    <Edit className="w-4 h-4" />
                    C'est votre entreprise ? Modifier ma fiche
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 bg-white border-t border-lagoon-100">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <p className="text-gray-500 text-sm">© 2026 Annuaire Polynésien</p>
          <Link to="/" className="text-lagoon hover:text-lagoon-600 text-sm">Accueil</Link>
        </div>
      </footer>
    </div>
  );
}
